import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Play } from "lucide-react";

interface GalleryItem {
  image: string;
  title: string;
  venue: string;
  alt: string;
  slug: string;
  galleryType: string;
}

export function FeaturedGalleries() {
  const [galleries, setGalleries] = useState<GalleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        // First fetch featured galleries, then recent ones to fill to 6
        const { data: featuredData, error: featuredError } = await supabase
          .from("galleries")
          .select("*")
          .eq("is_published", true)
          .eq("is_featured", true)
          .order("event_date", { ascending: false })
          .limit(6);

        if (featuredError) throw featuredError;

        let allGalleries = featuredData || [];

        // If we don't have 6 featured, fetch more recent ones
        if (allGalleries.length < 6) {
          const featuredIds = allGalleries.map((g) => g.id);
          const remainingCount = 6 - allGalleries.length;

          const { data: recentData, error: recentError } = await supabase
            .from("galleries")
            .select("*")
            .eq("is_published", true)
            .not("id", "in", featuredIds.length > 0 ? `(${featuredIds.join(",")})` : "(00000000-0000-0000-0000-000000000000)")
            .order("event_date", { ascending: false })
            .limit(remainingCount);

          if (!recentError && recentData) {
            allGalleries = [...allGalleries, ...recentData];
          }
        }

        if (allGalleries.length > 0) {
          setGalleries(
            allGalleries.map((g) => ({
              image: g.featured_image_url || "",
              title: g.title,
              venue: g.venue || "",
              alt: g.seo_description || g.title,
              slug: g.slug,
              galleryType: g.gallery_type,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching galleries:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGalleries();
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <section className="section-padding bg-muted/30">
        <div className="container-wide">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div>
              <Skeleton className="h-10 w-64" />
              <Skeleton className="mt-2 h-6 w-96" />
            </div>
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // No galleries - show coming soon
  if (galleries.length === 0) {
    return (
      <section className="section-padding bg-muted/30">
        <div className="container-wide">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div>
              <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
                Recent Weddings
              </h2>
              <p className="mt-2 text-muted-foreground">
                A glimpse into the love stories we've had the honor to capture.
              </p>
            </div>
            <Link
              to="/portfolio"
              className="text-primary font-medium hover:underline underline-offset-4"
            >
              View All Galleries →
            </Link>
          </div>
          <div className="mt-10 rounded-xl border border-border bg-card p-12 text-center">
            <p className="text-lg text-muted-foreground">
              Gallery coming soon! Check back to see our latest work.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section-padding bg-muted/30">
      <div className="container-wide">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div>
            <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Recent Weddings
            </h2>
            <p className="mt-2 text-muted-foreground">
              A glimpse into the love stories we've had the honor to capture.
            </p>
          </div>
          <Link
            to="/portfolio"
            className="text-primary font-medium hover:underline underline-offset-4"
          >
            View All Galleries →
          </Link>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {galleries.map((gallery, index) => (
            <motion.div
              key={gallery.title + index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={gallery.slug ? `/galleries/${gallery.slug}` : "/portfolio"}
                className="group relative block aspect-square overflow-hidden rounded-xl bg-muted"
              >
                {gallery.image ? (
                  <img
                    src={gallery.image}
                    alt={gallery.alt}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/10 to-muted">
                    <span className="font-serif text-2xl text-muted-foreground/50">{gallery.title}</span>
                  </div>
                )}
                {/* Play button overlay for video galleries */}
                {gallery.galleryType === "video" && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="h-8 w-8 text-foreground fill-current" />
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-primary-foreground opacity-0 transition-all duration-300 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0">
                  <h3 className="font-serif text-lg font-semibold">{gallery.title}</h3>
                  {gallery.venue && (
                    <p className="text-sm text-primary-foreground/80">{gallery.venue}</p>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
